import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { ImagePlus, Trash2, Copy, Images } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MediaTab = () => {
  const [items, setItems] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () =>
    axios
      .get(`${API}/media`, { withCredentials: true })
      .then((r) => setItems(r.data))
      .catch(() => setItems([]));

  useEffect(() => {
    load();
  }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      await axios.post(`${API}/media`, data, { withCredentials: true });
      toast.success(`${file.name} uploaded.`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const copyUrl = (m) => {
    const url = `${window.location.origin}${m.url}`;
    navigator.clipboard.writeText(url).then(() => toast.success("Image URL copied — paste it anywhere in the editors."));
  };

  const remove = async (m) => {
    if (!window.confirm(`Delete ${m.original_filename}?`)) return;
    try {
      await axios.delete(`${API}/media/${m.id}`, { withCredentials: true });
      toast.success("Image deleted.");
      load();
    } catch {
      toast.error("Could not delete the image.");
    }
  };

  return (
    <div data-testid="admin-media-tab">
      <div className="flex items-center justify-between mb-5 gap-4 flex-wrap">
        <p className="text-sm font-semibold text-slate-500">{items ? items.length : 0} files in the library</p>
        <button
          data-testid="media-upload-button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-2 rounded-full bg-[var(--brand)] text-white text-sm font-bold px-5 py-2.5 shadow-lg shadow-[var(--brand)]/30 transition-all duration-300 hover:bg-[var(--brand-dark)] disabled:opacity-60"
        >
          <ImagePlus className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload Image or Video"}
        </button>
        <input ref={fileRef} type="file" accept="image/*,video/*" className="hidden" onChange={upload} data-testid="media-file-input" />
      </div>

      {items === null ? (
        <div className="w-10 h-10 rounded-full border-4 border-[var(--brand)]/20 border-t-[var(--brand)] animate-spin" data-testid="media-loading-spinner" />
      ) : items.length === 0 ? (
        <div className="rounded-3xl bg-white border border-slate-100 p-14 text-center" data-testid="media-empty-state">
          <Images className="w-10 h-10 text-slate-300 mx-auto" />
          <p className="mt-4 font-display font-bold text-[#0b132b]">No uploads yet</p>
          <p className="mt-1 text-sm text-slate-400">Upload photos or logos, then copy their URLs into the Website Editor, Tours or Destinations.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="media-grid">
          {items.map((m) => (
            <div key={m.id} className="group rounded-2xl bg-white border border-slate-100 overflow-hidden shadow-[0_10px_30px_-16px_rgba(11,19,43,0.15)]" data-testid={`media-item-${m.id}`}>
              {(m.content_type || "").startsWith("video/") ? (
                <video src={m.url} muted loop autoPlay playsInline className="w-full h-32 object-cover" data-testid={`media-video-${m.id}`} />
              ) : (
                <img src={m.url} alt={m.original_filename} className="w-full h-32 object-cover" />
              )}
              <div className="p-3">
                <p className="text-xs font-bold text-[#0b132b] truncate">{m.original_filename}</p>
                <p className="text-[10px] text-slate-400">{Math.round(m.size / 1024)} KB</p>
                <div className="mt-2 flex gap-2">
                  <button
                    data-testid={`media-copy-${m.id}`}
                    onClick={() => copyUrl(m)}
                    className="flex-1 inline-flex items-center justify-center gap-1 rounded-full bg-[var(--brand-soft)] text-[var(--brand)] text-[11px] font-bold px-3 py-1.5 transition-colors hover:bg-[var(--brand)] hover:text-white"
                  >
                    <Copy className="w-3 h-3" /> Copy URL
                  </button>
                  <button
                    data-testid={`media-delete-${m.id}`}
                    onClick={() => remove(m)}
                    className="w-7 h-7 rounded-full bg-red-50 flex items-center justify-center text-red-500 hover:bg-red-500 hover:text-white transition-colors"
                    aria-label="Delete"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
